// Shared by the pool screen (water-kagee.js) and the night's window
// (meta/glass.js): mask smoothing and the night's gel colours. No three.js
// here, so the window can be drawn on a plain canvas.

// Separable [1 2 1] blur, twice, of up to four byte masks (0/1) into RGBA bytes.
export function blurMasks(w, h, masks, out) {
  const n = w * h;
  const tmp = new Float32Array(n), a = new Float32Array(n);
  for (let ch = 0; ch < masks.length; ch++) {
    const src = masks[ch];
    for (let i = 0; i < n; i++) a[i] = src[i];
    blurInPlace(w, h, a, tmp);
    for (let i = 0; i < n; i++) out[i * 4 + ch] = Math.round(Math.min(1, a[i]) * 255);
  }
}

// The same blur on one float field (values stay 0..1).
export function blurInPlace(w, h, a, tmp = new Float32Array(w * h)) {
  for (let pass = 0; pass < 2; pass++) {
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = x + y * w;
      tmp[i] = (a[i - (x > 0 ? 1 : 0)] + 2 * a[i] + a[i + (x < w - 1 ? 1 : 0)]) / 4;
    }
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = x + y * w;
      a[i] = (tmp[i - (y > 0 ? w : 0)] + 2 * tmp[i] + tmp[i + (y < h - 1 ? w : 0)]) / 4;
    }
  }
  return a;
}

// Each night's palette, keyed to the moon phase (0 new .. 4 full .. 7).
// A crab's gel is its shell colour pulled halfway toward its slot here, so
// pieces next to each other never go muddy.
export const NIGHT_PALETTES = [
  ['#7fd0c0', '#b48cff', '#5aa0ff', '#e6e0ff'],   // new moon: teal, violet, blue, silver
  ['#9fd16a', '#ff8a5c', '#ffc23a', '#b9a2ff'],
  ['#a8d86a', '#ff9070', '#ffcc50', '#c0a8ff'],
  ['#b8e070', '#ff9a70', '#ffd05a', '#c8b0ff'],
  ['#ffd27a', '#ff9d6b', '#ffe9b0', '#f0b0c8'],   // full moon: amber, coral, cream, rose
  ['#c0d870', '#f08a6a', '#f0c050', '#a8a0ff'],
  ['#9cd0a0', '#e08aa0', '#e8c060', '#a0a8ff'],
  ['#80c8b0', '#c090ff', '#70a8ff', '#d8d0f0'],
];

const hex = h => [1, 3, 5].map(k => parseInt(h.slice(k, k + 2), 16) / 255);
const toHex = c => '#' + c.map(v => Math.round(Math.max(0, Math.min(1, v)) * 255).toString(16).padStart(2, '0')).join('');
function hsl([r, g, b]) {
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2;
  if (mx === mn) return [0, 0, l];
  const d = mx - mn, s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
  const h = mx === r ? (g - b) / d + (g < b ? 6 : 0) : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h / 6, s, l];
}
function rgb(h, s, l) {
  const f = n => { const k = (n + h * 12) % 12, a = s * Math.min(l, 1 - l); return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1)); };
  return [f(0), f(8), f(4)];
}

// Gel ids: 0 lit by the rocks, 1..4 the crab that closed it, 5 all four, 6 the moon, 7 spare.
export function nightGelHex(phase, shellHex) {
  const pal = NIGHT_PALETTES[((phase % 8) + 8) % 8];
  const gels = ['#1f6f6a'];
  for (let i = 0; i < 4; i++) {
    const a = hex(shellHex[i]), b = hex(pal[i]);
    const [h, s] = hsl(a.map((v, k) => (v + b[k]) / 2));
    gels.push(toHex(rgb(h, Math.min(0.5, s * 0.62 + 0.06), 0.52)));
  }
  gels.push('#d99a3a', '#c8bf9c', '#ffffff');   // all four: deep gold (stays under the bloom), the moon: old paper
  return gels;
}
